'use client';

import type {
  MysteryRoomIncidentState,
  MysteryRoomLocalRuntime,
  MysteryRoomNpcState,
  MysteryRoomNpcTaskQueueEntry,
  MysteryRoomNpcRoleAction,
  MysteryRoomNpcTaskKind,
  MysteryRoomNpcTaskState,
} from '../core/localRuntime';
import type { MysteryRoomAnchorKind, MysteryRoomNpcRole, MysteryRoomVec2, MysteryRoomWorkAnchorDefinition } from '../world/mapTypes';
import { describeNpcBehaviorTree, npcUsesZombieBehavior } from './npcBehaviorTreeSystem';

type TaskCandidate = {
  incident: MysteryRoomIncidentState;
  taskKind: MysteryRoomNpcTaskKind;
  enterThreshold: number;
  exitThreshold: number;
  takeoverMargin: number;
  minHoldMs: number;
  targetNodeId: string | null;
  targetPoint?: MysteryRoomVec2 | null;
  targetZoneId?: string | null;
  anchorId?: string | null;
  anchorPoint?: MysteryRoomVec2 | null;
  taskAreaShape?: 'circle' | 'square';
  taskAreaRadius?: number;
};

type RoleDefaultTask = {
  taskKind: MysteryRoomNpcTaskKind;
  targetNodeId: string;
  score: number;
  enterThreshold: number;
  exitThreshold: number;
  takeoverMargin: number;
  minHoldMs: number;
};

const PATROL_REPLAN_MS = 12_000;

const ROLE_DEFAULT_TASKS: Record<MysteryRoomNpcRole, RoleDefaultTask> = {
  guard: {
    taskKind: 'patrol',
    targetNodeId: 'cross_center',
    score: 0.4,
    enterThreshold: 0.35,
    exitThreshold: 0.2,
    takeoverMargin: 0.1,
    minHoldMs: 1_400,
  },
  scientist: {
    taskKind: 'work',
    targetNodeId: 'research_work',
    score: 0.45,
    enterThreshold: 0.35,
    exitThreshold: 0.2,
    takeoverMargin: 0.08,
    minHoldMs: 2_200,
  },
  technician: {
    taskKind: 'work',
    targetNodeId: 'server_console',
    score: 0.4,
    enterThreshold: 0.3,
    exitThreshold: 0.18,
    takeoverMargin: 0.1,
    minHoldMs: 1_800,
  },
  medic: {
    taskKind: 'work',
    targetNodeId: 'med_station',
    score: 0.38,
    enterThreshold: 0.3,
    exitThreshold: 0.18,
    takeoverMargin: 0.08,
    minHoldMs: 1_800,
  },
  operator: {
    taskKind: 'work',
    targetNodeId: 'cross_center',
    score: 0.36,
    enterThreshold: 0.3,
    exitThreshold: 0.18,
    takeoverMargin: 0.08,
    minHoldMs: 1_800,
  },
};

export function roleActionForTask(
  npc: MysteryRoomNpcState,
  task: MysteryRoomNpcTaskState | null,
): MysteryRoomNpcRoleAction | null {
  if (!task) {
    return null;
  }

  switch (task.kind) {
    case 'repair_door':
      return npc.role === 'technician' ? 'repair_door' : null;
    case 'repair_server':
      return npc.role === 'technician' ? 'repair_server' : null;
    case 'repair_reactor':
      return npc.role === 'technician' ? 'repair_reactor' : null;
    case 'treat_injury':
      return npc.role === 'medic' ? 'treat_injury' : null;
    case 'trigger_alarm':
      return npc.role === 'guard' ? 'trigger_alarm' : null;
    case 'investigate':
      return npc.role === 'guard' ? 'investigate_intruder' : null;
    case 'evacuate':
      return npc.role === 'scientist' ? 'evacuate_lab' : npc.role === 'medic' || npc.role === 'operator' ? 'evacuate_if_clear' : null;
    case 'work':
      return npc.role === 'scientist' ? 'stop_experiment' : null;
    default:
      return null;
  }
}

export function chooseNpcTask(
  runtime: MysteryRoomLocalRuntime,
  npc: MysteryRoomNpcState,
  incidents = runtime.incidents,
): MysteryRoomNpcTaskState | null {
  if (npcUsesZombieBehavior(npc)) {
    return null;
  }

  const behaviorTree = describeNpcBehaviorTree(npc, runtime.worldAlarm.state === 'active');

  if (behaviorTree.includes('medical_support')) {
    const injuredIncident = pickHighestSeverityIncident(incidents, 'injured_person', npc.id, runtime.elapsedMs);
    if (injuredIncident) {
      return makeTask({
        incident: injuredIncident,
        taskKind: 'treat_injury',
        enterThreshold: 0.38,
        exitThreshold: 0.22,
        takeoverMargin: 0.08,
        minHoldMs: 2_000,
        targetNodeId: targetNodeForIncidentZone(injuredIncident.zoneId, 'med_station'),
        targetPoint: { ...injuredIncident.position },
        targetZoneId: injuredIncident.zoneId,
      });
    }
  }

  if (behaviorTree.includes('security_response')) {
    const alarmCallIncident = pickHighestSeverityIncident(incidents, 'alarm_call', npc.id, runtime.elapsedMs);
    if (alarmCallIncident) {
      return makeTask({
        incident: alarmCallIncident,
        taskKind: 'trigger_alarm',
        enterThreshold: 0.65,
        exitThreshold: 0.4,
        takeoverMargin: 0.14,
        minHoldMs: 1_200,
        targetNodeId: 'alarm_button_cross',
      });
    }
  }

  if (behaviorTree.includes('repair_support')) {
    const reactorIncident = pickHighestSeverityIncident(incidents, 'reactor_fault', npc.id, runtime.elapsedMs);
    if (reactorIncident) {
      return makeTask({
        incident: reactorIncident,
        taskKind: 'repair_reactor',
        enterThreshold: 0.94,
        exitThreshold: 0.78,
        takeoverMargin: 0.12,
        minHoldMs: 3_200,
        targetNodeId: targetNodeForIncidentZone(reactorIncident.zoneId, 'reactor_console'),
        ...targetAnchorPoint(runtime, pickNearestAnchor(runtime, npc, ['engineer_work', 'maintenance', 'breakdown'])),
      });
    }

    const serverIncident = pickHighestSeverityIncident(incidents, 'server_fault', npc.id, runtime.elapsedMs);
    if (serverIncident) {
      return makeTask({
        incident: serverIncident,
        taskKind: 'repair_server',
        enterThreshold: 0.55,
        exitThreshold: 0.5,
        takeoverMargin: 0.1,
        minHoldMs: 2_400,
        targetNodeId: targetNodeForIncidentZone(serverIncident.zoneId, 'server_console'),
        ...targetAnchorPoint(runtime, pickNearestAnchor(runtime, npc, ['engineer_work', 'maintenance', 'breakdown'])),
      });
    }

    const doorIncident = pickHighestSeverityIncident(incidents, 'door_failure', npc.id, runtime.elapsedMs);
    if (doorIncident) {
      return makeTask({
        incident: doorIncident,
        taskKind: 'repair_door',
        enterThreshold: 0.68,
        exitThreshold: 0.5,
        takeoverMargin: 0.1,
        minHoldMs: 2_200,
        targetNodeId: targetNodeForIncidentZone(doorIncident.zoneId, 'cross_center'),
        ...targetAnchorPoint(runtime, pickNearestAnchor(runtime, npc, ['engineer_work', 'maintenance', 'breakdown'])),
      });
    }
  }

  if (runtime.worldAlarm.state === 'active') {
    if (behaviorTree.includes('security_response')) {
      return makeTask({
        incident: buildWorldAlarmIncident(runtime, npc),
        taskKind: 'investigate',
        enterThreshold: 0.7,
        exitThreshold: 0.45,
        takeoverMargin: 0.15,
        minHoldMs: 2_400,
        targetNodeId: 'cross_center',
        ...targetAnchorPoint(runtime, pickNearestAnchor(runtime, npc, ['guard_work', 'patrol_point', 'checkpoint'])),
      });
    }

    const evacuationAnchor = pickNearestAnchor(runtime, npc, ['evacuation']);
    return makeTask({
      incident: buildWorldAlarmIncident(runtime, npc),
      taskKind: 'evacuate',
      enterThreshold: 0.75,
      exitThreshold: 0.5,
      takeoverMargin: 0.15,
      minHoldMs: 2_600,
      targetNodeId: npc.evacuationNodeId,
      ...targetAnchorPoint(runtime, evacuationAnchor),
    });
  }

  return null;
}

export function makeTask(candidate: TaskCandidate): MysteryRoomNpcTaskState {
  return {
    kind: candidate.taskKind,
    score: candidate.incident.severity,
    enterThreshold: candidate.enterThreshold,
    exitThreshold: candidate.exitThreshold,
    takeoverMargin: candidate.takeoverMargin,
    minHoldMs: candidate.minHoldMs,
    assignedIncidentId: candidate.incident.id,
    targetNodeId: candidate.targetNodeId,
    targetPoint: candidate.targetPoint ?? null,
    targetZoneId: candidate.targetZoneId ?? null,
    anchorId: candidate.anchorId ?? null,
    anchorPoint: candidate.anchorPoint ?? candidate.targetPoint ?? null,
    taskAreaShape: candidate.taskAreaShape,
    taskAreaRadius: candidate.taskAreaRadius,
  };
}

function buildWorldAlarmIncident(runtime: MysteryRoomLocalRuntime, npc: MysteryRoomNpcState): MysteryRoomIncidentState {
  return {
    id: runtime.worldAlarm.sourceIncidentId ?? 'world_alarm',
    kind: 'alarm',
    zoneId: npc.homeZoneId,
    position: { ...npc.position },
    sourceType: 'system',
    sourceActorId: 'system',
    severity: 1,
    status: 'active',
    assignedNpcId: null,
    leaseUntilMs: 0,
    createdAtMs: runtime.elapsedMs,
    resolvedAtMs: null,
    resolutionAction: null,
    resolutionRemainingMs: 0,
  };
}

export function defaultRoleTask(
  runtime: MysteryRoomLocalRuntime,
  npc: MysteryRoomNpcState,
): MysteryRoomNpcTaskState | null {
  if (npcUsesZombieBehavior(npc)) {
    return null;
  }

  if (!npcDutiesEnabled(runtime, npc)) {
    return null;
  }

  const behaviorTree = describeNpcBehaviorTree(npc, runtime.worldAlarm.state === 'active');
  if (behaviorTree[0] === 'hostile_target') {
    return makeWanderTask(runtime, npc, 0.34, 1_600);
  }

  const restroomAnchor = shouldVisitRestroom(runtime, npc) ? pickNearestAnchor(runtime, npc, ['restroom']) : null;
  if (restroomAnchor) {
    return makeRoutineTask(runtime, npc, restroomAnchor, 'toilet', 0.86, 12_000);
  }

  const restAnchor = shouldRest(runtime, npc) ? pickNearestAnchor(runtime, npc, ['break_room', 'standby']) : null;
  if (restAnchor) {
    return makeRoutineTask(runtime, npc, restAnchor, 'rest', 0.68, 18_000);
  }

  const guardPatrolAnchor = npc.role === 'guard' ? pickNextGuardPatrolAnchor(runtime, npc) : null;
  if (guardPatrolAnchor) {
    return makeRoutineTask(runtime, npc, guardPatrolAnchor, 'patrol', ROLE_DEFAULT_TASKS.guard.score, ROLE_DEFAULT_TASKS.guard.minHoldMs);
  }

  const roleAnchor = pickRoleAnchor(runtime, npc);
  if (roleAnchor) {
    return makeRoutineTask(
      runtime,
      npc,
      roleAnchor,
      roleAnchor.kind === 'patrol_point' || roleAnchor.kind === 'checkpoint' ? 'patrol' : 'work',
      ROLE_DEFAULT_TASKS[npc.role]?.score ?? 0.36,
      ROLE_DEFAULT_TASKS[npc.role]?.minHoldMs ?? 1_800,
    );
  }

  const defaults = ROLE_DEFAULT_TASKS[npc.role];
  if (!defaults) {
    return makeWanderTask(runtime, npc, 0.32, 1_600);
  }

  if (!runtime.map.navNodes.some((node) => node.id === defaults.targetNodeId)) {
    return makeWanderTask(runtime, npc, defaults.score, defaults.minHoldMs);
  }

  return {
    kind: defaults.taskKind,
    score: defaults.score,
    enterThreshold: defaults.enterThreshold,
    exitThreshold: defaults.exitThreshold,
    takeoverMargin: defaults.takeoverMargin,
    minHoldMs: defaults.minHoldMs,
    assignedIncidentId: null,
    targetNodeId: defaults.targetNodeId,
    targetPoint: null,
    targetZoneId: null,
    anchorId: null,
    anchorPoint: null,
  };
}

function makeWanderTask(
  runtime: MysteryRoomLocalRuntime,
  npc: MysteryRoomNpcState,
  score: number,
  minHoldMs: number,
): MysteryRoomNpcTaskState | null {
  const targetPoint = pickWanderPoint(runtime, npc);
  if (!targetPoint) {
    return null;
  }

  return {
    kind: 'wander',
    score,
    enterThreshold: 0.24,
    exitThreshold: 0.1,
    takeoverMargin: 0.06,
    minHoldMs,
    assignedIncidentId: null,
    targetNodeId: null,
    targetPoint,
    targetZoneId: npc.zoneId,
    anchorId: null,
    anchorPoint: { ...npc.position },
    taskAreaShape: 'circle',
    taskAreaRadius: 96,
  };
}

function pickWanderPoint(runtime: MysteryRoomLocalRuntime, npc: MysteryRoomNpcState): MysteryRoomVec2 | null {
  const room =
    runtime.map.rooms.find((entry) => entry.id === npc.zoneId) ??
    runtime.map.rooms.find((entry) => entry.floor.some((floor) =>
      npc.position.x >= floor.x &&
      npc.position.x <= floor.x + floor.width &&
      npc.position.y >= floor.y &&
      npc.position.y <= floor.y + floor.height,
    ));
  const floor = room?.floor[0];
  if (!floor) {
    return null;
  }

  const margin = Math.max(24, npc.radius + 16);
  const usableWidth = Math.max(1, floor.width - margin * 2);
  const usableHeight = Math.max(1, floor.height - margin * 2);

  for (let attempt = 0; attempt < 8; attempt += 1) {
    const seed = stableHash(`${npc.id}:wander:${Math.floor(runtime.elapsedMs / 10_000)}:${attempt}`);
    const point = {
      x: Math.round(floor.x + margin + usableWidth * (((seed >> 3) % 1000) / 1000)),
      y: Math.round(floor.y + margin + usableHeight * (((seed >> 13) % 1000) / 1000)),
    };
    if (Math.hypot(point.x - npc.position.x, point.y - npc.position.y) > npc.radius + 48) {
      return point;
    }
  }

  return {
    x: Math.round(floor.x + floor.width / 2),
    y: Math.round(floor.y + floor.height / 2),
  };
}

export function shouldReplaceCurrentTask(
  current: MysteryRoomNpcTaskState | null,
  challenger: MysteryRoomNpcTaskState | null,
  nowMs: number,
  lockedUntilMs = 0,
): boolean {
  if (!challenger) {
    return false;
  }

  if (challenger.score < challenger.enterThreshold) {
    return false;
  }

  if (!current) {
    return true;
  }

  if (sameTask(current, challenger)) {
    return false;
  }

  if (
    current.kind === 'patrol' &&
    challenger.kind === 'patrol' &&
    current.anchorId !== challenger.anchorId &&
    nowMs >= lockedUntilMs &&
    nowMs >= lockedUntilMs + PATROL_REPLAN_MS - current.minHoldMs
  ) {
    return true;
  }

  if (isEmergencyTask(challenger)) {
    return true;
  }

  if (nowMs < lockedUntilMs) {
    return false;
  }

  return challenger.score >= current.score + challenger.takeoverMargin;
}

export function shouldReleaseCurrentTask(
  runtime: MysteryRoomLocalRuntime,
  npc: MysteryRoomNpcState,
): boolean {
  const task = npc.currentTask;
  if (!task) {
    return false;
  }

  if (task.kind === 'patrol' || task.kind === 'work') {
    return false;
  }

  if (task.kind === 'rest' || task.kind === 'toilet' || task.kind === 'wander') {
    return true;
  }

  if (task.kind === 'evacuate' || task.kind === 'investigate') {
    if (task.kind === 'evacuate' && !task.assignedIncidentId) {
      return npc.health >= npc.maxHealth;
    }
    return runtime.worldAlarm.state !== 'active' || task.assignedIncidentId !== runtime.worldAlarm.sourceIncidentId;
  }

  if (!task.assignedIncidentId) {
    return false;
  }

  const incident = runtime.incidents.find((entry) => entry.id === task.assignedIncidentId);
  if (!incident || incident.status === 'resolved') {
    return true;
  }

  if (task.kind === 'trigger_alarm') {
    return incident.kind !== 'alarm_call';
  }

  return incident.severity < task.exitThreshold;
}

function sameTask(
  current: MysteryRoomNpcTaskState,
  challenger: MysteryRoomNpcTaskState,
) {
  return (
    current.kind === challenger.kind &&
    current.targetNodeId === challenger.targetNodeId &&
    current.anchorId === challenger.anchorId &&
    current.assignedIncidentId === challenger.assignedIncidentId
  );
}

function isEmergencyTask(task: MysteryRoomNpcTaskState) {
  return task.kind === 'evacuate' || task.kind === 'investigate' || task.kind === 'trigger_alarm';
}

function makeRoutineTask(
  runtime: MysteryRoomLocalRuntime,
  npc: MysteryRoomNpcState,
  anchor: MysteryRoomWorkAnchorDefinition,
  taskKind: MysteryRoomNpcTaskKind,
  score: number,
  minHoldMs: number,
): MysteryRoomNpcTaskState {
  const target = targetAnchorPoint(runtime, anchor);
  return {
    kind: taskKind,
    score,
    enterThreshold: 0.28,
    exitThreshold: 0.12,
    takeoverMargin: 0.08,
    minHoldMs,
    assignedIncidentId: null,
    targetNodeId: target.targetNodeId,
    targetPoint: routineScatterPoint(anchor, npc, runtime.elapsedMs, taskKind),
    targetZoneId: anchor.zoneId,
    anchorId: anchor.id,
    anchorPoint: { ...anchor.position },
    taskAreaShape: anchor.taskAreaShape ?? 'circle',
    taskAreaRadius: anchor.taskAreaRadius ?? defaultTaskAreaRadius(taskKind),
  };
}

function pickRoleAnchor(runtime: MysteryRoomLocalRuntime, npc: MysteryRoomNpcState) {
  const assignedAnchors = runtime.map.workAnchors.filter((anchor) => npc.workAnchorIds.includes(anchor.id));
  const direct = pickNearestAnchorFromList(assignedAnchors, npc.position, roleAnchorKinds(npc.role));
  if (direct) {
    return direct;
  }

  return pickNearestAnchor(runtime, npc, roleAnchorKinds(npc.role));
}

function pickNextGuardPatrolAnchor(runtime: MysteryRoomLocalRuntime, npc: MysteryRoomNpcState) {
  const assignedIds = new Set(npc.workAnchorIds);
  const assignedPatrolAnchors = runtime.map.workAnchors.filter((anchor) =>
    assignedIds.has(anchor.id) &&
    (anchor.kind === 'patrol_point' || anchor.kind === 'checkpoint') &&
    anchorAllowedForNpc(anchor, npc),
  );
  const patrolAnchors = assignedPatrolAnchors.length > 0
    ? assignedPatrolAnchors
    : runtime.map.workAnchors.filter((anchor) =>
        (anchor.kind === 'patrol_point' || anchor.kind === 'checkpoint') &&
        anchorAllowedForNpc(anchor, npc),
      );

  if (patrolAnchors.length === 0) {
    return null;
  }

  const currentAnchorId = npc.currentTask?.kind === 'patrol' ? npc.currentTask.anchorId : null;
  const currentIndex = currentAnchorId ? patrolAnchors.findIndex((anchor) => anchor.id === currentAnchorId) : -1;
  if (currentIndex >= 0) {
    return patrolAnchors[(currentIndex + 1) % patrolAnchors.length] ?? patrolAnchors[0];
  }

  return pickNearestAnchorFromList(patrolAnchors, npc.position, ['patrol_point', 'checkpoint']);
}

function roleAnchorKinds(role: MysteryRoomNpcRole): MysteryRoomAnchorKind[] {
  switch (role) {
    case 'guard':
      return ['patrol_point', 'checkpoint', 'guard_work', 'armory'];
    case 'medic':
      return ['medic_work', 'medical_station', 'workstation'];
    case 'technician':
      return ['engineer_work', 'maintenance', 'breakdown', 'standby'];
    case 'operator':
      return ['operator_work', 'workstation', 'armory'];
    case 'scientist':
    default:
      return ['experiment', 'workstation'];
  }
}

function pickNearestAnchor(
  runtime: MysteryRoomLocalRuntime,
  npc: MysteryRoomNpcState,
  kinds: MysteryRoomAnchorKind[],
) {
  return pickNearestAnchorFromList(
    runtime.map.workAnchors.filter((anchor) => anchorAllowedForNpc(anchor, npc)),
    npc.position,
    kinds,
  );
}

function pickNearestAnchorFromList(
  anchors: MysteryRoomWorkAnchorDefinition[],
  position: MysteryRoomVec2,
  kinds: MysteryRoomAnchorKind[],
) {
  const allowedKinds = new Set(kinds);
  let best: MysteryRoomWorkAnchorDefinition | null = null;
  let bestDistance = Number.POSITIVE_INFINITY;

  for (const anchor of anchors) {
    if (!allowedKinds.has(anchor.kind)) {
      continue;
    }
    const distance = Math.hypot(anchor.position.x - position.x, anchor.position.y - position.y);
    if (distance < bestDistance) {
      best = anchor;
      bestDistance = distance;
    }
  }

  return best;
}

function anchorAllowedForNpc(anchor: MysteryRoomWorkAnchorDefinition, npc: MysteryRoomNpcState) {
  return anchor.allowedRoles.length === 0 || anchor.allowedRoles.includes(npc.role);
}

function targetAnchorPoint(
  runtime: MysteryRoomLocalRuntime,
  anchor: MysteryRoomWorkAnchorDefinition | null,
) {
  if (!anchor) {
    return {};
  }

  return {
    targetNodeId: nearestNavNodeId(runtime, anchor),
    targetPoint: { ...anchor.position },
    targetZoneId: anchor.zoneId,
    anchorId: anchor.id,
    anchorPoint: { ...anchor.position },
    taskAreaShape: anchor.taskAreaShape ?? 'circle',
    taskAreaRadius: anchor.taskAreaRadius ?? 84,
  };
}

function nearestNavNodeId(runtime: MysteryRoomLocalRuntime, anchor: MysteryRoomWorkAnchorDefinition) {
  let bestNodeId: string | null = null;
  let bestDistance = Number.POSITIVE_INFINITY;

  for (const node of runtime.map.navNodes) {
    if (node.zoneId !== anchor.zoneId) {
      continue;
    }
    const distance = Math.hypot(node.position.x - anchor.position.x, node.position.y - anchor.position.y);
    if (distance < bestDistance) {
      bestNodeId = node.id;
      bestDistance = distance;
    }
  }

  return bestDistance <= 80 ? bestNodeId : null;
}

function shouldRest(runtime: MysteryRoomLocalRuntime, npc: MysteryRoomNpcState) {
  if (runtime.elapsedMs < 30_000) {
    return false;
  }
  const cycle = 70_000 + stableHash(npc.id) % 20_000;
  return ((runtime.elapsedMs + stableHash(`${npc.id}:rest`)) % cycle) > cycle * 0.72;
}

function shouldVisitRestroom(runtime: MysteryRoomLocalRuntime, npc: MysteryRoomNpcState) {
  if (runtime.elapsedMs < 90_000) {
    return false;
  }
  const cycle = 260_000 + stableHash(npc.id) % 90_000;
  return ((runtime.elapsedMs + stableHash(`${npc.id}:toilet`)) % cycle) > cycle * 0.94;
}

function routineScatterPoint(
  anchor: MysteryRoomWorkAnchorDefinition,
  npc: MysteryRoomNpcState,
  nowMs: number,
  taskKind: MysteryRoomNpcTaskKind,
) {
  const radius = Math.max(npc.radius + 12, anchor.taskAreaRadius ?? defaultTaskAreaRadius(taskKind));
  const seed = stableHash(`${npc.id}:${taskKind}:${Math.floor(nowMs / 12_000)}`);
  const angle = (seed % 628) / 100;
  const minDistance =
    taskKind === 'toilet'
      ? Math.min(radius, npc.radius + 6)
      : taskKind === 'rest'
        ? Math.min(radius, npc.radius + 20)
        : Math.min(radius, npc.radius + 36);
  if (anchor.taskAreaShape === 'square') {
    const xSign = (seed & 1) === 0 ? -1 : 1;
    const ySign = (seed & 2) === 0 ? -1 : 1;
    const x = xSign * (minDistance + Math.max(0, radius - minDistance) * (((seed >> 4) % 100) / 100));
    const y = ySign * (minDistance + Math.max(0, radius - minDistance) * (((seed >> 11) % 100) / 100));
    return {
      x: Math.round(anchor.position.x + x),
      y: Math.round(anchor.position.y + y),
    };
  }
  const distance = minDistance + Math.max(0, radius - minDistance) * (((seed >> 4) % 100) / 100);
  return {
    x: Math.round(anchor.position.x + Math.cos(angle) * distance),
    y: Math.round(anchor.position.y + Math.sin(angle) * distance),
  };
}

function defaultTaskAreaRadius(taskKind: MysteryRoomNpcTaskKind) {
  return taskKind === 'toilet' ? 28 : taskKind === 'rest' ? 96 : 84;
}

export function npcDutiesEnabled(runtime: MysteryRoomLocalRuntime, npc: MysteryRoomNpcState): boolean {
  switch (npc.dutyActivation) {
    case 'manual':
      return false;
    case 'trigger':
      return npc.dutyTriggerIds.some((triggerId) =>
        runtime.triggers.some((trigger) => trigger.id === triggerId && trigger.lastActivatedAtMs !== null),
      );
    case 'door_open':
      return npc.dutyDoorIds.some((doorId) =>
        runtime.doors.some((door) => door.id === doorId && (door.state === 'opening' || door.state === 'open')),
      );
    case 'world_alarm':
      return runtime.worldAlarm.state === 'active';
    case 'auto':
    default:
      return true;
  }
}

export function buildNpcTaskQueue(runtime: MysteryRoomLocalRuntime): MysteryRoomNpcTaskQueueEntry[] {
  return runtime.npcs
    .filter((npc) => npc.alive && !npcUsesZombieBehavior(npc))
    .map((npc) => {
      if (npc.currentTask) {
        return {
          id: `${npc.id}:${npc.currentTask.kind}:${npc.currentTask.anchorId ?? npc.currentTask.targetNodeId ?? 'target'}`,
          npcId: npc.id,
          npcName: npc.name,
          role: npc.role,
          kind: npc.currentTask.kind,
          state: 'active',
          label: npcTaskLabel(npc.currentTask.kind),
          anchorId: npc.currentTask.anchorId ?? null,
          targetZoneId: npc.currentTask.targetZoneId ?? npc.zoneId,
          activation: npc.dutyActivation,
        };
      }

      if (!npcDutiesEnabled(runtime, npc)) {
        return {
          id: `${npc.id}:duty_wait`,
          npcId: npc.id,
          npcName: npc.name,
          role: npc.role,
          kind: 'duty_wait',
          state: 'waiting',
          label: dutyWaitLabel(npc),
          anchorId: null,
          targetZoneId: npc.homeZoneId,
          activation: npc.dutyActivation,
        };
      }

      return {
        id: `${npc.id}:idle`,
        npcId: npc.id,
        npcName: npc.name,
        role: npc.role,
        kind: 'duty_wait',
        state: 'idle',
        label: 'Ожидает подходящую задачу',
        anchorId: null,
        targetZoneId: npc.zoneId,
        activation: npc.dutyActivation,
      };
    });
}

function npcTaskLabel(kind: MysteryRoomNpcTaskKind) {
  switch (kind) {
    case 'work':
      return 'Рабочая обязанность';
    case 'patrol':
      return 'Патруль';
    case 'rest':
      return 'Отдых';
    case 'toilet':
      return 'Туалет';
    case 'evacuate':
      return 'Эвакуация';
    case 'investigate':
      return 'Расследование тревоги';
    case 'repair_door':
    case 'repair_server':
    case 'repair_reactor':
      return 'Ремонт';
    case 'treat_injury':
      return 'Лечение раненого';
    case 'trigger_alarm':
      return 'Включение тревоги';
    case 'neutralize_intruder':
      return 'Нейтрализация угрозы';
    case 'wander':
    default:
      return 'Свободное перемещение';
  }
}

function dutyWaitLabel(npc: MysteryRoomNpcState) {
  switch (npc.dutyActivation) {
    case 'manual':
      return 'Обязанности выключены вручную';
    case 'trigger':
      return npc.dutyTriggerIds.length > 0 ? 'Ждет связанный триггер' : 'Ждет триггер, но он не выбран';
    case 'door_open':
      return npc.dutyDoorIds.length > 0 ? 'Ждет открытие двери' : 'Ждет дверь, но она не выбрана';
    case 'world_alarm':
      return 'Ждет тревогу';
    case 'auto':
    default:
      return 'Готов к обязанностям';
  }
}

function stableHash(value: string) {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) >>> 0;
  }
  return hash;
}

function targetNodeForIncidentZone(zoneId: string, fallbackNodeId: string) {
  switch (zoneId) {
    case 'airlock_entry':
      return 'airlock_inner';
    case 'research_lab':
      return 'research_work';
    case 'med_bay':
      return 'med_station';
    case 'server_room':
      return 'server_console';
    case 'reactor_room':
      return 'reactor_console';
    case 'central_cross':
      return 'cross_center';
    default:
      return fallbackNodeId;
  }
}

function pickHighestSeverityIncident(
  incidents: MysteryRoomIncidentState[],
  kind: MysteryRoomIncidentState['kind'],
  npcId: string,
  nowMs: number,
): MysteryRoomIncidentState | null {
  let bestIncident: MysteryRoomIncidentState | null = null;

  for (const incident of incidents) {
    if (
      incident.kind !== kind ||
      incident.status === 'resolved' ||
      !incidentIsAvailableToNpc(incident, npcId, nowMs)
    ) {
      continue;
    }

    if (!bestIncident || incident.severity > bestIncident.severity) {
      bestIncident = incident;
    }
  }

  return bestIncident;
}

function incidentIsAvailableToNpc(
  incident: MysteryRoomIncidentState,
  npcId: string,
  nowMs: number,
) {
  if (!incident.assignedNpcId) {
    return true;
  }

  if (incident.assignedNpcId === npcId) {
    return true;
  }

  return incident.leaseUntilMs <= nowMs;
}
